// Listing modes for Purchase Orders — List / Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/quotes/modes.ts and src/blank/BlankLayout.tsx (Rule 0).
//
//   <base>/list      → OrderList     (the working register)
//   <base>/process   → OrderProcess  (drop a CSV or PDF, extract parts, generate PO)
//
// ── PROCESS IS g2-ONLY, AND THAT IS DELIBERATE ───────────────────────────────
// One image serves every tenant (Dockerfile.cloudrun), so an ungated Process
// mode would put a process mode and switcher onto the purchase orders register of
// all seven tenants. On anything but g2 / aquipe this module keeps the single-view
// register.
import { getProduct } from "../config/product";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in Orders.tsx.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Purchase order register",
  process: "Draft POs from files",
};

/**
 * List, not Process. The register IS the primary working surface.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<module>.viewMode`, underscored — convention followed across Aeronet.
 */
export const MODE_STORAGE_KEY = "orders.viewMode";

/**
 * Does this tenant get the mode switcher?
 * G2 and Aquipe tenants get the mode switcher.
 */
export const hasModeSwitcher = (): boolean =>
  getProduct() === "g2" || getProduct() === "aquipe";

export function isModeSegment(segment: string): boolean {
  return Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);
}

/**
 * Read the remembered mode from localStorage, validated against MODE_LABELS.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
